import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sparkles, Copy, Check, RefreshCw, X, Plus, Layers, Link2, Globe2, Target } from 'lucide-react';
import AutoCarouselButton from './AutoCarouselButton';
import GoalRegenerateChip from './GoalRegenerateChip';
import { toast } from 'sonner';
import { linkedge, api } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import { TONE_OPTIONS } from '../../lib/toneOptions';
import { POST_COUNTRY_OPTIONS, resolveDefaultCountry } from '../../lib/countries';

/**
 * Prompt-driven post generation tab.
 *
 * Mirrors the regenerate-post dialog controls (topic + custom topics chips +
 * count slider + words-per-post slider + tones + research URLs) but inline
 * in its own tab. The parent can also invoke `triggerGenerate()` via ref so
 * the page-level "Regenerate posts" button works tab-aware.
 */
const PromptPostsTab = forwardRef(function PromptPostsTab({ analysisId, existing, defaults }, ref) {
  const [topic, setTopic] = useState(defaults?.topic || '');
  const [count, setCount] = useState(defaults?.count || 4);
  const [wordsEach, setWordsEach] = useState(defaults?.words_each || 175);
  const [customTopics, setCustomTopics] = useState(defaults?.custom_topics || []);
  const [topicInput, setTopicInput] = useState('');
  const [tones, setTones] = useState(defaults?.tones || []);
  const [urlsText, setUrlsText] = useState((defaults?.extra_urls || []).join('\n'));
  // "Connect to my functional expertise" - when ON (default) the post is
  // anchored to the user's Post Context (industry, function, ICP). Switch
  // OFF for off-topic / non-category posts where forcing the seat anchor
  // would feel contrived (e.g. reacting to Satya Nadella's "token capital"
  // framing without bending the post back to ABM/revops).
  const [connectToExpertise, setConnectToExpertise] = useState(
    defaults?.connect_to_expertise !== undefined ? defaults.connect_to_expertise : true,
  );
  const [country, setCountry] = useState(resolveDefaultCountry(defaults?.country));
  const [goal, setGoal] = useState(defaults?.goal || existing?.goal || '');
  const [goalCatalog, setGoalCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(existing || null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [lastError, setLastError] = useState(null);

  // Fetch the goal → framework playbook once so the picker can render
  // human-readable labels + preview each framework.
  useEffect(() => {
    let alive = true;
    api.get('/posts/goals').then((r) => {
      if (alive) setGoalCatalog(r?.data?.goals || []);
    }).catch(() => { /* non-fatal, picker just shows keys */ });
    return () => { alive = false; };
  }, []);

  const activeGoal = goalCatalog.find((g) => g.key === goal) || null;

  useEffect(() => {
    if (defaults && !data) {
      setTopic(defaults.topic || '');
      setCount(defaults.count || 4);
      setWordsEach(defaults.words_each || 175);
      setCustomTopics(defaults.custom_topics || []);
      setTones(defaults.tones || []);
      setUrlsText((defaults.extra_urls || []).join('\n'));
      if (defaults.connect_to_expertise !== undefined) {
        setConnectToExpertise(defaults.connect_to_expertise);
      }
      setCountry(resolveDefaultCountry(defaults.country));
      if (defaults.goal !== undefined) setGoal(defaults.goal || '');
    }
  }, [defaults, data]);

  const buildPayload = () => {
    const extra_urls = urlsText
      .split(/\r?\n/)
      .map((x) => x.trim())
      .filter(Boolean)
      .slice(0, 10);
    return {
      topic: topic.trim() || undefined,
      count,
      words_each: wordsEach,
      custom_topics: customTopics,
      tones,
      extra_urls,
      connect_to_expertise: connectToExpertise,
      country_override: country,
      goal: goal || undefined,
    };
  };

  const handleGenerate = async () => {
    // Nudge — not block — the user to pick a goal. If none picked, warn
    // and fall through to the generic writer (which still uses the
    // master prompt). This keeps power users unblocked.
    if (!goal) {
      toast.warning('Pick a goal for a sharper post — using the generic writer for now.', { duration: 2200 });
    }
    setLoading(true);
    setLastError(null);
    try {
      const res = await linkedge.generateManusPosts(analysisId, buildPayload());
      const payload = res?.result || res;
      setData(payload);
      const n = payload?.posts?.length || 0;
      if (n === 0) toast.warning('No posts returned — try a different topic.');
      else toast.success(`Generated ${n} posts.`);
    } catch (e) {
      const msg = getErrorMessage(e, 'Generation failed');
      setLastError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Expose triggerGenerate() to parent so the top "Regenerate posts" button
  // can re-run this tab's generation with current form state.
  useImperativeHandle(ref, () => ({ triggerGenerate: handleGenerate, isLoading: () => loading }), [
    topic,
    count,
    wordsEach,
    customTopics,
    tones,
    urlsText,
    connectToExpertise,
    country,
    loading,
  ]);

  const addTopic = () => {
    const v = topicInput.trim();
    if (!v) return;
    if (!customTopics.includes(v)) setCustomTopics([...customTopics, v]);
    setTopicInput('');
  };

  const toggleTone = (key) =>
    setTones((prev) => (prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]));

  const handleCopy = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopiedIdx(idx);
      toast.success('Copied');
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch {
      toast.error('Copy failed');
    }
  };

  const posts = data?.posts || [];

  return (
    <div className="space-y-6" data-testid="prompt-posts-tab">
      {/* Top-of-page primary CTA so the Generate button is ALWAYS
          visible above the fold, not just at the bottom of the
          long config card. */}
      <Card className="border-primary/25 bg-primary/[0.04]" data-testid="prompt-posts-top-cta">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div className="min-w-0">
            <p className="font-mono-edge text-[10px] tracking-[0.18em] text-primary">PROMPT POSTS</p>
            <h2 className="font-display text-base sm:text-lg tracking-[-0.01em] mt-0.5">
              Draft {count} voice-matched post{count === 1 ? '' : 's'} from your prompt
            </h2>
            <p className="text-[12.5px] text-muted-foreground mt-0.5">
              Uses the config below. Adjust topic, sub-topics, tones or URLs, then hit Generate.
            </p>
          </div>
          <Button
            onClick={handleGenerate}
            disabled={loading}
            className="shrink-0 rounded-full"
            size="lg"
            data-testid="prompt-generate-btn-top"
          >
            {loading ? (
              <><RefreshCw size={14} className="mr-2 animate-spin" /> Generating…</>
            ) : (
              <><Sparkles size={14} className="mr-2" /> Generate {count} post{count === 1 ? '' : 's'}</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Persistent error card — in-place retry so the user doesn't have
          to scroll back to the config to try again. */}
      {!loading && lastError && (
        <Card
          className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10"
          data-testid="prompt-error-retry-card"
        >
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div className="flex items-start gap-2.5 min-w-0">
              <div className="mt-0.5 shrink-0 rounded-full bg-[hsl(var(--warning))]/25 p-1">
                <Sparkles size={14} className="text-[hsl(var(--warning))]" />
              </div>
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold">Post generation didn&rsquo;t finish</p>
                <p className="text-[12.5px] text-muted-foreground mt-0.5 break-words">{lastError}</p>
              </div>
            </div>
            <Button
              onClick={handleGenerate}
              className="shrink-0 rounded-full"
              size="sm"
              data-testid="prompt-error-retry-btn"
            >
              <RefreshCw size={13} className="mr-1.5" /> Try again
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-border">
        <CardContent className="p-5 space-y-5">
          {/* Goal picker — choose the framework FIRST. Each goal maps to
              a proven LinkedIn writing framework (PASE, OEIR, SRTL, …)
              that the backend enforces during generation. Read the
              product-owner playbook at
              /app/memory/linkedin-post-frameworks.md for the source of
              this list. */}
          <div className="space-y-2" data-testid="prompt-goal-block">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs font-medium flex items-center gap-1.5">
                <Target size={12} className="text-primary" /> What should this post do?
              </Label>
              {activeGoal && (
                <Badge variant="secondary" className="text-[10.5px] font-mono-edge tracking-[0.12em]">
                  {activeGoal.framework_name}
                </Badge>
              )}
            </div>
            <p className="text-[11.5px] text-muted-foreground">
              We pick the writing framework that matches your goal so the post is built for the reader response you want.
            </p>
            <Select value={goal} onValueChange={setGoal}>
              <SelectTrigger className="h-10" data-testid="prompt-goal-select">
                <SelectValue placeholder="Pick a goal — recommended" />
              </SelectTrigger>
              <SelectContent>
                {goalCatalog.map((g) => (
                  <SelectItem key={g.key} value={g.key} data-testid={`prompt-goal-option-${g.key}`}>
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeGoal && (
              <div className="rounded-lg border border-primary/20 bg-primary/[0.04] px-3 py-2.5 text-[11.5px] leading-snug" data-testid="prompt-goal-preview">
                <p><span className="font-semibold text-foreground">Framework — {activeGoal.framework_name}:</span></p>
                <ol className="mt-1 list-decimal list-inside space-y-0.5 text-muted-foreground">
                  {(activeGoal.framework_steps || []).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ol>
                <p className="mt-1.5 text-muted-foreground">
                  <span className="font-semibold text-foreground">Reader response:</span> {activeGoal.reader_response}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="prompt-topic" className="text-xs font-medium">
              Topic / brief
            </Label>
            <Textarea
              id="prompt-topic"
              data-testid="prompt-topic-input"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="What should the posts be about? Leave empty to use the auto-inferred Post Context…"
              rows={3}
              className="text-sm resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="prompt-custom-topics" className="text-xs font-medium">
              Specific sub-topics (optional)
            </Label>
            <p className="text-[11px] text-muted-foreground">
              Press Enter to add. Each will inform at least one post.
            </p>
            <div className="flex flex-wrap gap-1.5 min-h-[1.75rem]">
              {customTopics.map((t, i) => (
                <Badge
                  key={`${t}-${i}`}
                  variant="secondary"
                  className="rounded-md gap-1 pr-1 font-normal"
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => setCustomTopics(customTopics.filter((_, j) => j !== i))}
                    className="ml-0.5 rounded hover:bg-foreground/10 p-0.5"
                    aria-label="Remove"
                  >
                    <X size={11} />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                id="prompt-custom-topics"
                value={topicInput}
                onChange={(e) => setTopicInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTopic();
                  }
                }}
                placeholder="e.g. AI in B2B sales qualification"
                className="h-9 text-sm"
                data-testid="prompt-custom-topic-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={addTopic} className="h-9 shrink-0">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">How many posts</Label>
                <span className="text-sm font-mono-edge font-semibold">{count}</span>
              </div>
              <Slider
                min={1}
                max={20}
                step={1}
                value={[count]}
                onValueChange={(v) => setCount(v[0])}
                data-testid="prompt-count-slider"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Words per post</Label>
                <span className="text-sm font-mono-edge font-semibold" data-testid="prompt-words-value">
                  ~{wordsEach}{' '}
                  <span className="text-[10px] text-muted-foreground tracking-[0.16em]">
                    {wordsEach <= 200 ? 'SHORT' : 'LONG'}
                  </span>
                </span>
              </div>
              <Slider
                min={100}
                max={400}
                step={25}
                value={[wordsEach]}
                onValueChange={(v) => setWordsEach(v[0])}
                data-testid="prompt-words-slider"
              />
              <p className="text-[11px] text-muted-foreground">
                Range: 100–400 words. ≤200 uses compressed thought-leadership structure (scene + hidden problem + close). 220–400 uses the full structure with research support.
              </p>
            </div>
          </div>

          {/* Country / region context picker ----------------------------- */}
          <div
            className="flex items-start justify-between gap-4 rounded-lg border border-border bg-secondary/40 px-3.5 py-3"
            data-testid="prompt-country-row"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Globe2 size={13} className="text-primary shrink-0" />
                <Label
                  htmlFor="prompt-country-select"
                  className="text-sm font-medium cursor-pointer"
                >
                  Country / region context
                </Label>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                Anchors examples, currency, spelling, and regulators to this market. Saved as the default for this analysis.
              </p>
            </div>
            <div className="w-44 shrink-0">
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger id="prompt-country-select" data-testid="prompt-country-select">
                  <SelectValue placeholder="Pick a country" />
                </SelectTrigger>
                <SelectContent>
                  {POST_COUNTRY_OPTIONS.map((c) => (
                    <SelectItem
                      key={c.value}
                      value={c.value}
                      data-testid={`prompt-country-option-${c.value.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* "Connect to my functional expertise" toggle ---------------- */}
          <div
            className="flex items-start justify-between gap-4 rounded-lg border border-border bg-secondary/40 px-3.5 py-3"
            data-testid="prompt-connect-expertise-row"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Link2 size={13} className="text-primary shrink-0" />
                <Label
                  htmlFor="prompt-connect-expertise"
                  className="text-sm font-medium cursor-pointer"
                >
                  Connect to my functional expertise
                </Label>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                {connectToExpertise
                  ? 'Posts will be written from your seat (industry, function, ICP) and tie ideas back to your day-job expertise.'
                  : 'Posts will treat the topic on its own. The writer will NOT shoehorn the topic back to your day-job category - good for off-topic / personal reactions.'}
              </p>
            </div>
            <Switch
              id="prompt-connect-expertise"
              checked={connectToExpertise}
              onCheckedChange={setConnectToExpertise}
              data-testid="prompt-connect-expertise-toggle"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Tones (optional rotation)</Label>
              {tones.length > 0 && (
                <button
                  type="button"
                  className="text-[11px] text-muted-foreground hover:text-foreground"
                  onClick={() => setTones([])}
                >
                  Clear ({tones.length})
                </button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {tones.length === 0
                ? 'Pick any subset to rotate; leave empty for a balanced mix.'
                : `Posts will rotate through your ${tones.length} selected tone${tones.length === 1 ? '' : 's'}.`}
            </p>
            <div className="flex flex-wrap gap-2">
              {TONE_OPTIONS.map((t) => {
                const selected = tones.includes(t.key);
                return (
                  <button
                    type="button"
                    key={t.key}
                    onClick={() => toggleTone(t.key)}
                    className={`rounded-full px-3 py-1.5 text-xs border transition-colors ${
                      selected
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-card text-foreground/80 border-border hover:border-primary/50'
                    }`}
                    data-testid={`prompt-tone-${t.key}`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="prompt-extra-urls" className="text-xs font-medium">
              Research URLs (optional)
            </Label>
            <p className="text-[11px] text-muted-foreground">
              Up to 10 article / report / post URLs. We extract them as context for the writer.
            </p>
            <Textarea
              id="prompt-extra-urls"
              value={urlsText}
              onChange={(e) => setUrlsText(e.target.value)}
              placeholder={'https://stratechery.com/...\nhttps://hbr.org/...'}
              rows={3}
              className="text-sm font-mono-edge resize-none"
              data-testid="prompt-extra-urls"
            />
          </div>

          <div className="flex items-center gap-3 flex-wrap pt-1">
            <Button
              onClick={handleGenerate}
              disabled={loading}
              data-testid="prompt-generate-btn"
              className="rounded-full"
            >
              {loading ? (
                <>
                  <RefreshCw size={14} className="mr-2 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Sparkles size={14} className="mr-2" />
                  Generate {count} post{count === 1 ? '' : 's'}
                </>
              )}
            </Button>
            {data?.generated_at && (
              <span className="text-[11px] text-muted-foreground font-mono-edge">
                Last run: {new Date(data.generated_at).toLocaleString()}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {loading && posts.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            Writing your posts… this usually takes 30–60 seconds.
          </CardContent>
        </Card>
      )}

      {!loading && posts.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No posts yet. Configure the prompt above and hit Generate.
          </CardContent>
        </Card>
      )}

      {posts.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          {posts.map((p, i) => (
            <Card key={p.index || i} className="border-border" data-testid={`prompt-post-${i + 1}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-edge text-[10px] text-primary tracking-[0.18em]">
                      POST {String(p.index || i + 1).padStart(2, '0')}
                    </span>
                    {p.title && <span className="text-sm font-semibold leading-tight">{p.title}</span>}
                    {(p.goal || goal) && (() => {
                      const usedKey = p.goal || goal;
                      const fw = goalCatalog.find((g) => g.key === usedKey)?.framework_name;
                      return fw ? (
                        <Badge variant="outline" className="text-[10px] font-mono-edge tracking-[0.10em] px-1.5 py-0" data-testid={`prompt-post-framework-${i + 1}`}>
                          {fw}
                        </Badge>
                      ) : null;
                    })()}
                  </div>
                  <div className="flex items-center gap-1">
                    <GoalRegenerateChip
                      analysisId={analysisId}
                      source="prompt"
                      index={i}
                      currentGoal={p.goal || goal || null}
                      tones={tones}
                      compact
                      onReplaced={(np) => {
                        const next = [...posts];
                        next[i] = { ...next[i], ...np };
                        setData((d) => ({ ...(d || {}), posts: next }));
                      }}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopy(p.text, i)}
                      data-testid={`prompt-post-copy-${i + 1}`}
                    >
                      {copiedIdx === i ? <Check size={14} /> : <Copy size={14} />}
                    </Button>
                    <AutoCarouselButton
                      analysisId={analysisId}
                      sourceType="prompt"
                      sourceIdx={i}
                      variant="outline"
                      size="sm"
                      postPreview={p.text || p.content || ''}
                    />
                  </div>
                </div>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{p.text}</p>
                <div className="pt-2 border-t border-border/60 text-[11px] text-muted-foreground font-mono-edge inline-flex items-center gap-2">
                  <Layers size={11} />
                  {p.word_count || (p.text || '').split(/\s+/).filter(Boolean).length} words
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
});

export default PromptPostsTab;
